import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  FileText, 
  Rocket, 
  Info,
  MessageCircle,
  AlertTriangle,
  Calendar,
  User,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
    deleteNotification 
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon and color for notification type
  const getNotificationStyle = (type, notification = {}) => {
    switch (type) {
      case 'new_chat_message':
      case 'chat':
        return { 
          icon: <MessageCircle className="w-4 h-4" />, 
          bg: 'bg-blue-100', 
          color: 'text-blue-600',
          action: notification.action_url || '/chat'
        };
      case 'step_advanced':
      case 'step_completed':
        return { 
          icon: <Rocket className="w-4 h-4" />, 
          bg: 'bg-emerald-100', 
          color: 'text-emerald-600',
          action: notification.action_url || '/dashboard'
        };
      case 'document_uploaded':
      case 'document_approved':
      case 'document_request':
      case 'documents_complete':
        return { 
          icon: <FileText className="w-4 h-4" />, 
          bg: 'bg-purple-100', 
          color: 'text-purple-600',
          action: notification.action_url || '/documents'
        };
      case 'case_created':
      case 'case_update':
        return { 
          icon: <Check className="w-4 h-4" />, 
          bg: 'bg-emerald-100', 
          color: 'text-emerald-600',
          action: '/dashboard'
        };
      case 'interview_reminder':
      case 'appointment':
        return { 
          icon: <Calendar className="w-4 h-4" />, 
          bg: 'bg-amber-100', 
          color: 'text-amber-600',
          action: '/dashboard'
        };
      case 'alert':
      case 'urgent':
        return { 
          icon: <AlertTriangle className="w-4 h-4" />, 
          bg: 'bg-red-100', 
          color: 'text-red-600',
          action: '/dashboard'
        };
      case 'client_assigned':
      case 'oficial_assigned':
        return { 
          icon: <User className="w-4 h-4" />, 
          bg: 'bg-indigo-100', 
          color: 'text-indigo-600',
          action: '/dashboard'
        };
      default:
        return { 
          icon: <Info className="w-4 h-4" />, 
          bg: 'bg-navy-100', 
          color: 'text-navy-600',
          action: null
        };
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const handleNotificationClick = (notification) => {
    // Mark as read first
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate - prioritize action_url if available
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.type === 'new_chat_message' || notification.type === 'chat') {
      navigate('/chat');
    } else {
      const style = getNotificationStyle(notification.type, notification);
      if (style.action) {
        navigate(style.action);
      }
    }
    
    // Close dropdown
    setIsOpen(false);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (deleteNotification) {
      deleteNotification(id);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-navy-100 rounded-lg transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-navy-600" />
        
        {/* Unread Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
              data-testid="unread-count-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-lg border border-navy-100 overflow-hidden z-50"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-navy-100 flex items-center justify-between bg-navy-50">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-navy-600" />
                <span className="font-semibold text-navy-900">Notificaciones</span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                    {unreadCount} nuevas
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-navy-500 hover:text-emerald-600"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Marcar todas
                </Button>
              )}
            </div>

            {/* Notifications List */}
            <ScrollArea className="max-h-96">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-10 h-10 text-navy-200 mx-auto mb-3" />
                  <p className="text-navy-500 text-sm">No tienes notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-navy-50">
                  {notifications.slice(0, 20).map((notification) => {
                    const style = getNotificationStyle(notification.type, notification);
                    return (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-4 py-3 hover:bg-navy-50 cursor-pointer transition-colors group ${
                          !notification.read ? 'bg-emerald-50/50' : ''
                        }`}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg} ${style.color}`}>
                            {style.icon}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${
                              !notification.read ? 'font-medium text-navy-900' : 'text-navy-700'
                            }`}>
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-navy-400">
                                {formatTimeAgo(notification.sent_at || notification.created_at)}
                              </p>
                              {style.action && (
                                <span className="text-xs text-emerald-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <ExternalLink className="w-3 h-3" />
                                  Abrir
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Unread indicator or delete */}
                          <div className="flex items-center gap-1">
                            {!notification.read && (
                              <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-2 border-t border-navy-100 bg-navy-50">
                <button 
                  onClick={() => { navigate('/notifications'); setIsOpen(false); }}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Ver todas las notificaciones →
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
